import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Stack,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Save as SaveIcon,
  Folder as FolderIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import {
  CaseEditorStepFrame,
  caseManagerInsetSurfaceSx,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
} from '../caseManagerStyles';
import AnalyticsTagsField from '../AnalyticsTagsField';

const CaseContentStep = ({
  data,
  onUpdate,
  validationErrors = [],
  scoringConfig,
  scoringFields,
  scoringTotal,
  scoringIsBalanced,
  onScoringChange,
  onResetScoring,
  formatWeightPercent,
  allowPublicTemplates = false
}) => {
  const [itemEditingKey, setItemEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const [expandedSection, setExpandedSection] = useState('');
  const [itemDialog, setItemDialog] = useState({
    open: false,
    sectionIndex: -1,
    itemIndex: -1,
    item: null
  });
  const [templateDialog, setTemplateDialog] = useState({
    open: false,
    mode: 'save', // 'save' or 'load'
    name: '',
    templates: [],
    loading: false,
    allowIncomplete: false,
    visibility: 'private'
  });

  const resolvedWeights = scoringConfig?.resolved || {};
  const defaultWeights = scoringConfig?.defaults || {};
  const hasScoringOverrides = Boolean(scoringConfig?.overrides);

  // Helper function to check if a section has validation errors
  const getSectionErrors = (sectionIndex) => {
    return validationErrors.filter(error =>
      error.includes(`Section "${data.sections[sectionIndex]?.title}"`) ||
      error.includes('section')
    );
  };

  // Helper function to check if there are content validation errors
  const hasContentErrors = () => {
    return validationErrors.some(error =>
      error.includes('section') ||
      error.includes('item') ||
      error.includes('complete')
    );
  };

  // Create new item with default values
  const createNewItem = (sectionType, order) => ({
    id: `item_${Date.now()}_${order}`,
    itemId: `${sectionType}${order + 1}`,
    title: '',
    maxMarks: 2,
    markingCriteria: '',
    extendedInstructions: '',
    patientResponse: '',
    domainCodes: [],
    competencyCodes: [],
    safetyCritical: false,
    omissionEligible: true,
    taxonomyVersion: '1',
    order
  });

  // Utility: 0 -> A, 1 -> B … 25 -> Z, 26 -> AA …
  const toLetters = (n) => {
    let s = '';
    while (n >= 0) {
      s = String.fromCharCode((n % 26) + 65) + s;
      n = Math.floor(n / 26) - 1;
    }
    return s;
  };

  const createNewSection = (order) => {
    const customCount =
      data.sections?.filter(s => s.isCustom).length || 0;

    const customType = `CUST_${toLetters(customCount)}`;

    return {
      id: `section_${Date.now()}`,
      title: '',
      sectionType: customType,
      order,
      isCustom: true,
      items: []
    };
  };

  const isItemComplete = (item) => {
    return item.title?.trim() && item.markingCriteria?.trim() && item.extendedInstructions?.trim();
  };

  const isSectionComplete = (section) => {
    if (!section.items || section.items.length === 0) return false;
    return section.items.every(item => isItemComplete(item));
  };

  const isSectionEmpty = (section) => {
    return !section.items || section.items.length === 0;
  };

  // Preset sections that can be added
  const presetSections = [
    { title: 'Intro/Consent', sectionType: 'IC', isCustom: false },
    { title: 'Presenting Complaint', sectionType: 'PC', isCustom: false },
    { title: 'History of PC', sectionType: 'HPC', isCustom: false },
    { title: 'Past Medical History', sectionType: 'PMH', isCustom: false },
    { title: 'Family History', sectionType: 'FH', isCustom: false },
    { title: 'Drug History', sectionType: 'DH', isCustom: false },
    { title: 'Social History', sectionType: 'SH', isCustom: false },
    { title: 'Systems Review', sectionType: 'SR', isCustom: false },
    { title: 'Ideas, Concerns, Expectations', sectionType: 'ICE', isCustom: false },
    { title: 'Flow', sectionType: 'FLOW', isCustom: false },
    { title: 'Empathy', sectionType: 'EMPATHY', isCustom: false }
  ];

  // Template management functions
  const openSaveTemplateDialog = async () => {
    setTemplateDialog({
      open: true,
      mode: 'save',
      name: '',
      templates: [],
      loading: false,
      allowIncomplete: false,
      visibility: 'private'
    });
  };

  const openLoadTemplateDialog = async () => {
    setTemplateDialog({
      open: true,
      mode: 'load',
      name: '',
      templates: [],
      loading: true,
      allowIncomplete: false,
      visibility: 'private'
    });

    // Load templates
    try {
      const response = await api.get('/platform/templates');
      setTemplateDialog(prev => ({
        ...prev,
        templates: response.data,
        loading: false
      }));
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplateDialog(prev => ({
        ...prev,
        templates: [],
        loading: false
      }));
    }
  };

  const closeTemplateDialog = () => {
    setTemplateDialog({
      open: false,
      mode: 'save',
      name: '',
      templates: [],
      loading: false,
      allowIncomplete: false,
      visibility: 'private'
    });
  };

  const validateTemplateForSaving = (sections) => {
    const validSections = sections.filter(section => {
      // Section must have a title and sectionType
      if (!section.title || !section.sectionType) return false;

      // If allowIncomplete is false, all items must be complete
      if (!templateDialog.allowIncomplete && section.items) {
        return section.items.every(item =>
          item.title && item.markingCriteria && item.extendedInstructions
        );
      }

      return true;
    });

    return validSections;
  };

  const saveAsTemplate = async () => {
    if (!templateDialog.name.trim()) {
      alert('Please enter a template name');
      return;
    }

    try {
      setTemplateDialog(prev => ({ ...prev, loading: true }));

      const validSections = validateTemplateForSaving(data.sections);

      if (validSections.length === 0) {
        alert('No valid sections to save as template');
        setTemplateDialog(prev => ({ ...prev, loading: false }));
        return;
      }

      const templateData = {
        name: templateDialog.name,
        sections: validSections,
        allowIncomplete: templateDialog.allowIncomplete,
        visibility: allowPublicTemplates ? templateDialog.visibility : 'private'
      };

      await api.post('/platform/templates', templateData);

      alert('Template saved successfully!');
      closeTemplateDialog();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template: ' + (error.response?.data?.message || error.message));
    } finally {
      setTemplateDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const loadTemplate = async (template) => {
    if (window.confirm('Loading this template will override your current content. Continue?')) {
      try {
        onUpdate({ sections: template.sections });
        closeTemplateDialog();
      } catch (error) {
        console.error('Error loading template:', error);
        alert('Failed to load template');
      }
    }
  };

  const deleteTemplate = async (template) => {
    if (!template.permissions?.canDelete) {
      alert('You do not have permission to delete this template.');
      return;
    }

    if (template.isDefault) {
      alert('Cannot delete the default template');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/platform/templates/${template._id}`);
      // Refresh the templates list
      const response = await api.get('/platform/templates');
      setTemplateDialog(prev => ({
        ...prev,
        templates: response.data
      }));
      alert('Template deleted successfully!');
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template: ' + (error.response?.data?.message || error.message));
    }
  };

  const moveSectionUp = (sectionIndex) => {
    if (sectionIndex === 0) return;

    const newSections = [...data.sections];
    const temp = newSections[sectionIndex];
    newSections[sectionIndex] = newSections[sectionIndex - 1];
    newSections[sectionIndex - 1] = temp;

    const updatedSections = newSections.map((section, index) => ({
      ...section,
      order: index
    }));

    onUpdate({ sections: updatedSections });
  };

  const moveSectionDown = (sectionIndex) => {
    if (sectionIndex === data.sections.length - 1) return;

    const newSections = [...data.sections];
    const temp = newSections[sectionIndex];
    newSections[sectionIndex] = newSections[sectionIndex + 1];
    newSections[sectionIndex + 1] = temp;

    const updatedSections = newSections.map((section, index) => ({
      ...section,
      order: index
    }));

    onUpdate({ sections: updatedSections });
  };

  const moveItemUp = (sectionIndex, itemIndex) => {
    if (itemIndex === 0) return;

    const newSections = [...data.sections];
    const section = { ...newSections[sectionIndex] };
    const newItems = [...section.items];

    const temp = newItems[itemIndex];
    newItems[itemIndex] = newItems[itemIndex - 1];
    newItems[itemIndex - 1] = temp;

    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index,
      itemId: `${section.sectionType}${index + 1}`
    }));

    section.items = updatedItems;
    newSections[sectionIndex] = section;
    onUpdate({ sections: newSections });
  };

  const moveItemDown = (sectionIndex, itemIndex) => {
    const section = data.sections[sectionIndex];
    if (itemIndex === section.items.length - 1) return;

    const newSections = [...data.sections];
    const newSection = { ...newSections[sectionIndex] };
    const newItems = [...newSection.items];

    const temp = newItems[itemIndex];
    newItems[itemIndex] = newItems[itemIndex + 1];
    newItems[itemIndex + 1] = temp;

    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index,
      itemId: `${newSection.sectionType}${index + 1}`
    }));

    newSection.items = updatedItems;
    newSections[sectionIndex] = newSection;
    onUpdate({ sections: newSections });
  };

  const addSection = (preset = null) => {
    const newSection = preset
      ? { ...preset, id: `section_${Date.now()}`, order: data.sections.length, items: [] }
      : createNewSection(data.sections.length);

    onUpdate({ sections: [...data.sections, newSection] });
  };

  const deleteSection = (sectionIndex) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      const newSections = data.sections.filter((_, index) => index !== sectionIndex);
      const updatedSections = newSections.map((section, index) => ({
        ...section,
        order: index
      }));
      onUpdate({ sections: updatedSections });
    }
  };

  const addItem = (sectionIndex) => {
    const newSections = [...data.sections];
    const section = { ...newSections[sectionIndex] };
    const newItem = createNewItem(section.sectionType, section.items.length);

    section.items = [...section.items, newItem];
    newSections[sectionIndex] = section;
    onUpdate({ sections: newSections });

    setItemDialog({
      open: true,
      sectionIndex,
      itemIndex: section.items.length - 1,
      item: { ...newItem }
    });
  };

  const editItem = (sectionIndex, itemIndex) => {
    const item = data.sections[sectionIndex].items[itemIndex];
    setItemDialog({
      open: true,
      sectionIndex,
      itemIndex,
      item: { ...item }
    });
  };

  const saveItem = () => {
    const { sectionIndex, itemIndex, item } = itemDialog;

    const newSections = [...data.sections];
    const section = { ...newSections[sectionIndex] };

    section.items = [...section.items];
    section.items[itemIndex] = item;
    newSections[sectionIndex] = section;

    onUpdate({ sections: newSections });
    setItemDialog({ open: false, sectionIndex: -1, itemIndex: -1, item: null });
  };

  const deleteItem = (sectionIndex, itemIndex) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      const newSections = [...data.sections];
      const section = { ...newSections[sectionIndex] };

      section.items = section.items.filter((_, index) => index !== itemIndex);

      section.items = section.items.map((item, index) => ({
        ...item,
        order: index,
        itemId: `${section.sectionType}${index + 1}`
      }));

      newSections[sectionIndex] = section;
      onUpdate({ sections: newSections });
    }
  };

  const updateSectionTitle = (sectionIndex, title) => {
    const section = data.sections[sectionIndex];
    if (!section.isCustom) return; // Only custom sections can have titles changed

    const newSections = [...data.sections];
    newSections[sectionIndex] = {
      ...newSections[sectionIndex],
      title
    };
    onUpdate({ sections: newSections });
  };

  return (
    <CaseEditorStepFrame>

      {/* Content Validation Errors */}
      {hasContentErrors() && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Content Issues Detected:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
            All sections must have complete items (title, marking criteria, and extended instructions) before you can proceed.
          </Typography>
        </Alert>
      )}

      {/* Template Management */}
      <Paper sx={{ ...caseManagerInsetSurfaceSx, p: 2.2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Template Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Save your current case structure as a template for future use, or load an existing template.
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<SaveIcon />}
            onClick={openSaveTemplateDialog}
            sx={publicSecondaryButtonSx}
          >
            Save as Template
          </Button>
          <Button
            startIcon={<FolderIcon />}
            onClick={openLoadTemplateDialog}
            sx={publicSecondaryButtonSx}
          >
            Load Template
          </Button>
        </Box>
      </Paper>

      {/* Add Section Controls */}
      <Paper sx={{ ...caseManagerInsetSurfaceSx, p: 2.2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {data.sections.length === 0 ? 'Select Sections for Your Case' : 'Add More Sections'}
        </Typography>
        {data.sections.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which sections you want to include in your case. You can add items to each section after selecting it.
          </Typography>
        )}
        <Box display="flex" flexWrap="wrap" gap={1}>
          {presetSections.map((preset) => (
            <Button
              key={preset.sectionType}
              variant="outlined"
              size="small"
              onClick={() => addSection(preset)}
              disabled={data.sections.some(s => s.sectionType === preset.sectionType)}
              sx={publicSecondaryButtonSx}
            >
              {preset.title}
            </Button>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => addSection()}
            sx={publicPrimaryButtonSx}
          >
            Custom Section
          </Button>
        </Box>
      </Paper>

      {/* Sections */}
      <Box>
        {data.sections.length === 0 ? (
          <Paper sx={{ ...caseManagerInsetSurfaceSx, p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No sections selected yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose sections above to start building your case content
            </Typography>
          </Paper>
        ) : (
          data.sections.map((section, sectionIndex) => {
            const sectionErrors = getSectionErrors(sectionIndex);
            const isEmpty = isSectionEmpty(section);
            const isComplete = isSectionComplete(section);
            const hasIncompleteItems = section.items?.some(item => !isItemComplete(item));

            return (
              <Box key={section.id} sx={{ mb: 2 }}>
                <Accordion
                  expanded={expandedSection === section.id}
                  onChange={(_, isExpanded) => setExpandedSection(isExpanded ? section.id : '')}
                  sx={{
                    border: 2,
                    borderColor: sectionErrors.length > 0 || isEmpty || hasIncompleteItems
                      ? '#f44336'
                      : isComplete
                        ? 'success.main'
                        : 'grey.300',
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" width="100%">
                      <Box flexGrow={1}>
                        {section.isCustom ? (
                          <Box display="flex" gap={2} alignItems="center">
                            <TextField
                              value={section.title}
                              onChange={(e) => updateSectionTitle(sectionIndex, e.target.value)}
                              variant="standard"
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Section Title"
                              size="small"
                              error={!section.title}
                            />
                          </Box>
                        ) : (
                          <Typography variant="h6">{section.title}</Typography>
                        )}
                        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                          <Chip
                            label={section.sectionType}
                            size="small"
                            color={section.isCustom ? 'secondary' : 'primary'}
                          />
                          {isEmpty && (
                            <Chip
                              label="Empty Section"
                              size="small"
                              color="error"
                            />
                          )}
                          {!isEmpty && (
                            <Chip
                              label={`${section.items?.filter(isItemComplete).length || 0}/${section.items?.length || 0} complete`}
                              size="small"
                              color={
                                isComplete ? 'success' : hasIncompleteItems ? 'error' : 'warning'
                              }
                            />
                          )}
                        </Box>
                        {sectionErrors.length > 0 && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {sectionErrors[0]}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            moveSectionUp(sectionIndex);
                          }}
                          disabled={sectionIndex === 0}
                          size="small"
                        >
                          <ArrowUpIcon />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            moveSectionDown(sectionIndex);
                          }}
                          disabled={sectionIndex === data.sections.length - 1}
                          size="small"
                        >
                          <ArrowDownIcon />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSection(sectionIndex);
                          }}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box width="100%">
                      {section.items.map((item, itemIndex) => {
                        const itemComplete = isItemComplete(item);
                        return (
                          <Paper
                            key={item.id}
                            sx={{
                              p: 2,
                              mb: 1,
                              border: 2,
                              borderColor: itemComplete ? 'success.main' : '#f44336',
                              bgcolor: itemComplete ? 'white' : '#ffebee',
                              opacity: 1
                            }}
                          >
                            <Box display="flex" alignItems="flex-start">
                              <Box flexGrow={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {item.itemId}: {item.title || 'Untitled Item'}
                                  </Typography>
                                  {!itemComplete && (
                                    <Chip
                                      label="Incomplete"
                                      size="small"
                                      color="error"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Max Marks: {item.maxMarks}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {item.markingCriteria ?
                                    (item.markingCriteria.substring(0, 100) + (item.markingCriteria.length > 100 ? '...' : '')) :
                                    'No marking criteria'
                                  }
                                </Typography>
                                {!itemComplete && (
                                  <Alert severity="warning" sx={{ mt: 1, fontSize: '0.8rem' }}>
                                    Complete title, marking criteria, and extended instructions to make this item valid.
                                  </Alert>
                                )}
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => moveItemUp(sectionIndex, itemIndex)}
                                  disabled={itemIndex === 0}
                                >
                                  <ArrowUpIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => moveItemDown(sectionIndex, itemIndex)}
                                  disabled={itemIndex === section.items.length - 1}
                                >
                                  <ArrowDownIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => editItem(sectionIndex, itemIndex)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => deleteItem(sectionIndex, itemIndex)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })}

                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => addItem(sectionIndex)}
                        sx={{ mt: 1, ...publicPrimaryButtonSx }}
                      >
                        Add Item
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })
        )}
      </Box>

      <Paper sx={{ ...caseManagerInsetSurfaceSx, p: 3, mt: 4, mb: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Scoring Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Adjust how each rubric contributes to the overall score. Totals must equal 100% before saving.
            </Typography>
          </Box>
          <Button
            disabled={!hasScoringOverrides}
            onClick={onResetScoring}
            sx={publicSecondaryButtonSx}
          >
            Reset to defaults
          </Button>
        </Box>

        {!scoringConfig ? (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading scoring configuration…
            </Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={3}>
              {scoringFields.map((field) => {
                const defaultValue = defaultWeights[field.key] ?? 0;
                const resolvedValue = resolvedWeights[field.key] ?? defaultValue;
                const isOverride = Math.abs(resolvedValue - defaultValue) > 0.0005;

                return (
                  <Paper
                    key={field.key}
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      {/* Left: Label and description */}
                      <Grid item xs={12} md={8}>
                        <Typography variant="subtitle1" fontWeight="600">
                          {field.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {field.description}
                        </Typography>
                        <Chip
                          size="small"
                          variant={isOverride ? 'filled' : 'outlined'}
                          color={isOverride ? 'warning' : 'default'}
                          label={`Default ${formatWeightPercent(defaultValue)}%`}
                          sx={{ mt: 1 }}
                        />
                      </Grid>

                      {/* Right: Editable weight input */}
                      <Grid item xs={12} md={4}>
                        <TextField
                          type="number"
                          label="Weight (%)"
                          fullWidth
                          size="small"
                          value={
                            itemEditingKey === field.key
                              ? editingValue
                              : formatWeightPercent(resolvedValue)
                          }
                          inputProps={{ min: 0, max: 100, step: 1 }}
                          onFocus={() => {
                            setItemEditingKey(field.key);
                            setEditingValue(formatWeightPercent(resolvedValue));
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setEditingValue(raw);
                            if (raw === '' || raw === '-') return; // allow temporary blanks
                            const n = Number(raw);
                            if (!Number.isNaN(n)) onScoringChange(field.key, n);
                          }}
                          onBlur={() => {
                            // finalize numeric conversion
                            const n = Number(editingValue);
                            if (!Number.isNaN(n)) onScoringChange(field.key, n);
                            setItemEditingKey(null);
                            setEditingValue('');
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Alert
              severity={scoringIsBalanced ? (hasScoringOverrides ? 'info' : 'success') : 'warning'}
              sx={{
                mt: 2,
                borderRadius: 2,
                fontWeight: 500,
              }}
            >
              Weighted total: {(scoringTotal * 100).toFixed(1)}% —{' '}
              {scoringIsBalanced
                ? hasScoringOverrides
                  ? 'Case-specific overrides are active for this configuration.'
                  : 'Using global default weights.'
                : 'Adjust the weights until the total equals 100%.'}
            </Alert>
          </>
        )}
      </Paper>

      {/* Item Edit Dialog */}
      <Dialog
        open={itemDialog.open}
        onClose={() => setItemDialog({ open: false, sectionIndex: -1, itemIndex: -1, item: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Edit Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item ID"
                value={itemDialog.item?.itemId || ''}
                disabled
                helperText="Auto-generated identifier"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Marks"
                value={itemDialog.item?.maxMarks || 2}
                onChange={(e) => setItemDialog({
                  ...itemDialog,
                  item: { ...itemDialog.item, maxMarks: parseInt(e.target.value) || 0 }
                })}
                inputProps={{ min: 0, max: 10 }}
                InputLabelProps={{ sx: { whiteSpace: 'nowrap' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={itemDialog.item?.title || ''}
                onChange={(e) => setItemDialog({
                  ...itemDialog,
                  item: { ...itemDialog.item, title: e.target.value }
                })}
                helperText="Brief description of what should be assessed"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Marking Criteria"
                value={itemDialog.item?.markingCriteria || ''}
                onChange={(e) => setItemDialog({
                  ...itemDialog,
                  item: { ...itemDialog.item, markingCriteria: e.target.value }
                })}
                helperText="What the student needs to do to get full marks"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Extended Instructions"
                value={itemDialog.item?.extendedInstructions || ''}
                onChange={(e) => setItemDialog({
                  ...itemDialog,
                  item: { ...itemDialog.item, extendedInstructions: e.target.value }
                })}
                helperText="Tell the AI exactly how to award or withhold marks for this item."
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Patient Notes (Optional)"
                value={itemDialog.item?.patientResponse || ''}
                onChange={(e) => setItemDialog({
                  ...itemDialog,
                  item: { ...itemDialog.item, patientResponse: e.target.value }
                })}
                helperText="Optional notes about how or when the patient should reveal extra details for this mark scheme item."
              />
            </Grid>

            <Grid item xs={12}>
              <AnalyticsTagsField
                domainCodes={itemDialog.item?.domainCodes || []}
                competencyCodes={itemDialog.item?.competencyCodes || []}
                onChange={({ domainCodes, competencyCodes }) => setItemDialog({
                  ...itemDialog,
                  item: {
                    ...itemDialog.item,
                    domainCodes,
                    competencyCodes,
                  }
                })}
                helperText="Choose stable skills such as allergy history, safeguarding or functional impact. Press Enter to keep a custom tag."
              />
            </Grid>

            {/* Add completion status info */}
            <Grid item xs={12}>
              <Alert
                severity={
                  itemDialog.item?.title?.trim() &&
                    itemDialog.item?.markingCriteria?.trim() &&
                    itemDialog.item?.extendedInstructions?.trim()
                    ? 'success'
                    : 'warning'
                }
                sx={{ mt: 1 }}
              >
                {itemDialog.item?.title?.trim() &&
                  itemDialog.item?.markingCriteria?.trim() &&
                  itemDialog.item?.extendedInstructions?.trim()
                  ? 'This item is complete and will be valid for case creation.'
                  : 'Fill in all required fields (title, marking criteria, and extended instructions) to make this item complete.'
                }
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialog({ open: false, sectionIndex: -1, itemIndex: -1, item: null })}>
            Cancel
          </Button>
          <Button onClick={saveItem} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog
        open={templateDialog.open}
        onClose={closeTemplateDialog}
        maxWidth="md"
        fullWidth
        disableBackdropClick={templateDialog.loading}
        disableEscapeKeyDown={templateDialog.loading}
      >
        <DialogTitle>
          {templateDialog.mode === 'save' ? 'Save Template' : 'Load Template'}
        </DialogTitle>
        <DialogContent>
          {templateDialog.mode === 'save' ? (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateDialog.name}
                onChange={(e) => setTemplateDialog({ ...templateDialog, name: e.target.value })}
                placeholder="Enter template name"
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth margin="dense" size="small">
                <InputLabel id="case-template-visibility-label">Visibility</InputLabel>
                <Select
                  labelId="case-template-visibility-label"
                  value={allowPublicTemplates ? templateDialog.visibility : 'private'}
                  label="Visibility"
                  onChange={(e) => setTemplateDialog({ ...templateDialog, visibility: e.target.value })}
                  disabled={!allowPublicTemplates}
                >
                  <MenuItem value="private">Private - only visible to you</MenuItem>
                  {allowPublicTemplates && (
                    <MenuItem value="public">Public - shared with your university team</MenuItem>
                  )}
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {!allowPublicTemplates
                  ? 'Templates are private to your account.'
                  : templateDialog.visibility === 'private'
                    ? 'Private templates are only visible to you.'
                    : 'Public templates are available to other admins and instructors within this university.'}
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={templateDialog.allowIncomplete}
                    onChange={(e) => setTemplateDialog({ ...templateDialog, allowIncomplete: e.target.checked })}
                  />
                }
                label="Allow saving incomplete items"
              />

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {templateDialog.allowIncomplete
                  ? 'Template will be saved with all sections, even if items are incomplete. You can fill in details later.'
                  : 'Only sections with complete items (title, marking criteria, and extended instructions) will be saved.'
                }
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {templateDialog.loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Loading templates...</Typography>
                </Box>
              ) : templateDialog.templates.length > 0 ? (
                <List>
                  {templateDialog.templates.map((template, index) => {
                    const isOwner = template.permissions?.isOwner;
                    const canDelete = template.permissions?.canDelete && !template.isDefault;
                    return (
                      <ListItem key={template._id || index} disablePadding secondaryAction={null}>
                        <ListItemButton onClick={() => loadTemplate(template)} sx={{ flex: 1 }}>
                          <ListItemText
                            primary={(
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle1">{template.name}</Typography>
                                <Chip
                                  label={template.visibility === 'private' ? 'Private' : 'Public'}
                                  color={template.visibility === 'private' ? 'secondary' : 'default'}
                                  size="small"
                                  variant={template.visibility === 'private' ? 'filled' : 'outlined'}
                                />
                                {isOwner && <Chip label="You" color="info" size="small" variant="outlined" />}
                              </Stack>
                            )}
                            secondary={`${template.sections.length} sections • Created: ${new Date(template.createdAt).toLocaleDateString()}`}
                          />
                        </ListItemButton>
                        <Tooltip
                          title={canDelete ? 'Delete template' : 'You cannot delete this template'}
                        >
                          <span>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteTemplate(template);
                              }}
                              color="error"
                              disabled={!canDelete}
                              aria-label="Delete template"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography>No templates available</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTemplateDialog} disabled={templateDialog.loading}>
            Cancel
          </Button>
          {templateDialog.mode === 'save' && (
            <Button
              onClick={saveAsTemplate}
              variant="contained"
              disabled={!templateDialog.name.trim() || templateDialog.loading}
            >
              {templateDialog.loading ? 'Saving...' : 'Save Template'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </CaseEditorStepFrame>
  );
};

export default CaseContentStep;

